import { useQuery } from '@tanstack/react-query';
import { FileBarChart2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EmptyState } from '@/components/shared/states';
import { PageHeader } from '@/components/shared/page-header';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useChurchStore } from '@/stores/church-store';

import { ReportFavoriteButton } from './report-favorite-button';
import { favoriteRefKey, useReportFavorites, type ReportFavoriteRef } from './report-favorites';
import { ReportsSubnav } from './reports-subnav';

interface SeededReportDefinition {
  key: string;
  category: 'finance' | 'people' | 'attendance' | 'communication' | 'governance';
  nameKey: string;
}

interface CustomReportRow {
  id: string;
  name: string;
  objectSource: string;
}

interface FavoriteRow {
  ref: ReportFavoriteRef;
  name: string;
  typeLabel: string;
  detailLabel: string;
  runPath: string;
}

export const ReportFavoritesPage = () => {
  const { t } = useTranslation('reports');
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const churchId = useChurchStore((state) => state.church?.id);
  const userId = useAuthStore((state) => state.user?.id);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const { favorites } = useReportFavorites(churchId, userId);

  const seededReports = useQuery({
    queryKey: ['reports', 'list'],
    queryFn: () => api.get<{ data: SeededReportDefinition[] }>('/reports'),
  });

  const customReports = useQuery({
    queryKey: ['reports', 'custom', 'favorites'],
    enabled: allowsFeature('custom_reports'),
    queryFn: () =>
      api.get<{ data: CustomReportRow[] }>('/reports/custom', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const rows = useMemo(() => {
    const seededByKey = new Map(
      (seededReports.data?.data ?? []).map((report) => [report.key, report]),
    );
    const customById = new Map(
      (customReports.data?.data ?? []).map((report) => [report.id, report]),
    );
    const resolved: FavoriteRow[] = [];

    for (const ref of favorites) {
      if (ref.kind === 'seeded') {
        const report = seededByKey.get(ref.key);
        if (!report) continue;
        resolved.push({
          ref,
          name: t(report.nameKey, report.key),
          typeLabel: t('favorites.typeReadyMade'),
          detailLabel: t(`category.${report.category}`),
          runPath: `/reports?report=${encodeURIComponent(report.key)}`,
        });
        continue;
      }

      const report = customById.get(ref.id);
      if (!report) continue;
      resolved.push({
        ref,
        name: report.name,
        typeLabel: t('favorites.typeCustom'),
        detailLabel: t(`custom.sources.${report.objectSource}`, report.objectSource),
        runPath: `/reports/custom/${report.id}`,
      });
    }

    return resolved;
  }, [customReports.data, favorites, seededReports.data, t]);

  const columns: DataTableColumn<FavoriteRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('custom.columns.name'),
        cell: (row) => (
          <div className="flex items-center gap-1">
            <span className="font-medium text-brand">{row.name}</span>
            <ReportFavoriteButton favoriteRef={row.ref} churchId={churchId} userId={userId} />
          </div>
        ),
      },
      {
        id: 'type',
        header: t('favorites.columns.type'),
        cell: (row) => row.typeLabel,
      },
      {
        id: 'detail',
        header: t('favorites.columns.detail'),
        cell: (row) => row.detailLabel,
        hideBelowMd: true,
      },
    ],
    [churchId, t, userId],
  );

  const loading = seededReports.isLoading || (allowsFeature('custom_reports') && customReports.isLoading);

  return (
    <div className="space-y-6">
      <PageHeader title={t('favorites.title')} description={t('favorites.description')} />
      <ReportsSubnav />

      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={<FileBarChart2 className="size-6" />}
          title={t('favorites.emptyTitle')}
          body={t('favorites.emptyBody')}
          action={
            <Button variant="secondary" onClick={() => navigate('/reports')}>
              {t('favorites.browseReadyMade')}
            </Button>
          }
        />
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => favoriteRefKey(row.ref)}
          loading={loading}
          empty={<p className="text-body-sm text-fg-secondary">{t('favorites.emptyTitle')}</p>}
          onRowClick={(row) => navigate(row.runPath)}
          rowActions={(row) => (
            <Button size="sm" variant="secondary" onClick={() => navigate(row.runPath)}>
              {t('custom.run')}
            </Button>
          )}
          actionsHeader={tCommon('table.actions')}
        />
      )}
    </div>
  );
};
